import api from '@/lib/api';

export interface QuestionTimingData {
  questionId: string;
  subject: string;
  topic: string;
  difficultyLevel: string;
  timeSpentMs: number;
  visitCount: number;
  firstVisitTime: string;
  lastVisitTime: string;
  wasAnswered: boolean;
  wasMarkedForReview: boolean;
}

export interface NavigationEvent {
  timestamp: string;
  fromQuestionId: string;
  toQuestionId: string;
  fromQuestionIndex: number;
  toQuestionIndex: number;
  navigationType: string;
  timeOnPreviousQuestionMs?: number;
}

export interface SubjectSwitchEvent {
  timestamp: string;
  fromSubject: string;
  toSubject: string;
  switchNumber: number;
  timeSinceLastSwitchMs?: number;
}

export interface AnswerChangeEvent {
  questionId: string;
  timestamp: string;
  previousAnswer: string;
  newAnswer: string;
  changeNumber: number;
  timeSinceQuestionFirstVisitMs: number;
}

export interface ReviewEvent {
  questionId: string;
  timestamp: string;
  marked: boolean;
  hadAnswer: boolean;
}

export interface FocusEvent {
  timestamp: string;
  eventType: string;
  durationMs?: number;
  currentQuestionId?: string;
}

export interface BehaviorMetrics {
  // Time metrics
  averageTimePerQuestionMs: number;
  fastestQuestionTimeMs: number;
  slowestQuestionTimeMs: number;
  timePerSubjectMs: Record<string, number>;
  timePerDifficultyMs: Record<string, number>;

  // Navigation metrics
  totalNavigations: number;
  forwardNavigations: number;
  backwardNavigations: number;
  jumpNavigations: number;
  subjectSwitches: number;

  // Answer change metrics
  totalAnswerChanges: number;
  questionsWithMultipleChanges: number;
  averageAnswerChangesPerQuestion: number;

  // Review metrics
  questionsMarkedForReview: number;
  questionsUnmarkedAfterReview: number;

  // Focus metrics
  focusLossCount: number;
  totalFocusLossDurationMs: number;
  longestFocusLossMs: number;

  // Strategic behavior
  usedSubjectGrouping: boolean;
  usedSequentialApproach: boolean;
  usedReviewStrategy: boolean;

  // Completion patterns
  questionsAnsweredInFirstPass: number;
  questionsAnsweredInReview: number;
  questionsLeftUnanswered: number;
}

export interface StudentBehaviorAnalytics {
  id: string;
  studentId: string;
  testId: string;
  testAssignmentId: string;
  tenantId: string;
  sessionStartTime: string;
  sessionEndTime: string;
  totalSessionDurationMs: number;
  questionTimings: QuestionTimingData[];
  navigationEvents: NavigationEvent[];
  subjectSwitches: SubjectSwitchEvent[];
  answerChanges: AnswerChangeEvent[];
  reviewEvents: ReviewEvent[];
  focusEvents: FocusEvent[];
  metrics: BehaviorMetrics;
  createdAt: string;
  updatedAt: string;
}

class AnalyticsService {
  /**
   * Get all behavior analytics for the current student
   */
  async getStudentAnalytics(): Promise<StudentBehaviorAnalytics[]> {
    try {
      const response = await api.get('/student/analytics');
      return response.data;
    } catch (error) {
      console.error('Error fetching student analytics:', error);
      throw error;
    }
  }

  /**
   * Get behavior analytics for a specific test
   */
  async getTestAnalytics(testId: string): Promise<StudentBehaviorAnalytics> {
    try {
      const response = await api.get(`/student/analytics/test/${testId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching test analytics:', error);
      throw error;
    }
  }

  /**
   * Format time in milliseconds to human-readable format
   */
  formatTime(ms: number): string {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }

  /**
   * Format percentage
   */
  formatPercentage(value: number, total: number): string {
    if (total === 0) return '0%';
    return `${Math.round((value / total) * 100)}%`;
  }
}

export const analyticsService = new AnalyticsService();

